import type { WorkspaceApp } from "@bafe/types";
import { formatBytes } from "@bafe/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { AccountStorageSummary } from "../../components/AccountStorageSummary";
import { Popup } from "../../components/Popup";
import { TeamSection } from "../settings/TeamSection";
import { api } from "../../lib/api";
import { presentError } from "../../lib/errors";
import { isForwarder, isMailboxAlias } from "../../lib/mail-addresses";
import { queryKeys } from "../../lib/query";
import { useSession } from "../../lib/session";
import { useOrganisationStore } from "../../lib/stores";

export function needsWorkspace(pathname: string): boolean {
	if (pathname === "/hub" || pathname.startsWith("/hub/")) return true;
	if (pathname === "/mail" || pathname.startsWith("/mail/")) return true;
	if (pathname === "/site" || pathname.startsWith("/site/")) return true;
	if (pathname === "/team" || pathname.startsWith("/team/")) return true;
	if (pathname === "/social" || pathname.startsWith("/social/")) return true;
	if (pathname === "/engage" || pathname.startsWith("/engage/")) return true;
	return (
		pathname.startsWith("/manage/domains") ||
		pathname.startsWith("/manage/access") ||
		pathname.startsWith("/manage/billing") ||
		pathname.startsWith("/manage/config")
	);
}

export function WorkspaceNeeded({ platformOwner }: { platformOwner: boolean }) {
	return (
		<div className="card">
			<h2>This screen belongs to a workspace</h2>
			<p className="sub">
				{platformOwner
					? "You are the platform owner, and this account is not a member of a workspace. Domains, mail, Hub, Site, Team, Social and Engage stay with the workspace that owns them."
					: "This account is not a member of a workspace yet. Create one, or ask an owner to invite you."}
			</p>
			<p className="stack">
				<Link className="btn" to="/manage/workspaces">
					View workspaces
				</Link>
				{platformOwner && (
					<Link className="btn" to="/manage/platform/mail/health">
						Platform health
					</Link>
				)}
			</p>
		</div>
	);
}

const APP_OPTIONS: Array<{ id: WorkspaceApp; label: string; detail: string }> = [
	{ id: "hub", label: "Hub", detail: "Tasks, people, sales and the rest of the business." },
	{ id: "mail", label: "Mail", detail: "Mailboxes, aliases, forwarders and migration." },
	{ id: "site", label: "Site", detail: "Sites, databases and redirects." },
	{ id: "team", label: "Team", detail: "Channels and direct messages for people in this workspace." },
	{ id: "social", label: "Social", detail: "Draft, approve and schedule posts on connected brand accounts." },
	{ id: "engage", label: "Engage", detail: "One inbox for WhatsApp, Telegram, Facebook Messenger, Instagram, Threads, X, TikTok and LinkedIn." },
];

export function AccessPage() {
	const session = useSession();
	if (!session.data) return <p className="empty">Loading access.</p>;
	return <TeamSection session={session.data} />;
}

export function WorkspacesPage() {
	const session = useSession();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const setOrganisationId = useOrganisationStore((state) => state.setOrganisationId);
	const [open, setOpen] = useState(false);
	const [name, setName] = useState("");
	const [error, setError] = useState<string | null>(null);

	const create = useMutation({
		mutationFn: () => api.createWorkspace(name.trim()),
		onSuccess: async (workspace) => {
			setOrganisationId(workspace.id);
			setName("");
			setOpen(false);
			await queryClient.invalidateQueries();
			navigate(`/manage/workspaces/${workspace.id}`);
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	function close() {
		if (create.isPending) return;
		setOpen(false);
		setError(null);
	}

	function onSubmit(event: FormEvent) {
		event.preventDefault();
		if (!name.trim()) return;
		setError(null);
		create.mutate();
	}

	const memberships = session.data?.memberships ?? [];
	const customers = useQuery({
		queryKey: ["admin-customers"],
		queryFn: () => api.adminSearchCustomers(""),
		enabled: Boolean(session.data?.platformOwner),
	});

	return (
		<div>
			<div className="row head-actions">
				<p className="sub">Each workspace has its own members, domains and apps.</p>
				<button
					className="btn"
					type="button"
					onClick={() => {
						setError(null);
						setOpen(true);
					}}
				>
					New workspace
				</button>
			</div>
			{memberships.length > 0 && (
				<div className="table-wrap">
					<table>
						<thead>
							<tr>
								<th>Workspace</th>
								<th>Role</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{memberships.map((membership) => (
								<tr key={membership.organisationId}>
									<td>{membership.organisationName}</td>
									<td className="quiet">{membership.role}</td>
									<td>
										<Link to={`/manage/workspaces/${membership.organisationId}`}>Apps</Link>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{session.data?.platformOwner && (
				<>
					<h2>All workspaces</h2>
					<p className="sub">
						Every workspace on this server. Opening its mail and records still requires membership.
					</p>
					{customers.isPending && <p className="empty">Loading workspaces.</p>}
					{customers.isError && <p className="err">{presentError(customers.error).title}</p>}
					{customers.data && (
						<div className="table-wrap">
							<table>
								<thead>
									<tr>
										<th>Workspace</th>
										<th>Status</th>
										<th>Plan</th>
										<th>Domains</th>
										<th>Mailboxes</th>
										<th>Storage</th>
									</tr>
								</thead>
								<tbody>
									{customers.data.data.map((customer) => (
										<tr key={customer.organisationId}>
											<td>{customer.name}</td>
											<td className="quiet">{customer.status}</td>
											<td className="quiet">{customer.planName ?? "No plan"}</td>
											<td className="quiet">{customer.domainCount}</td>
											<td className="quiet">{customer.mailboxCount}</td>
											<td className="quiet">
												{customer.usedBytes > 0 ? formatBytes(customer.usedBytes) : "0 B"}
												{customer.totalBytes > 0 ? ` of ${formatBytes(customer.totalBytes)}` : ""}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</>
			)}

			{memberships.length === 0 && !session.data?.platformOwner && (
				<p className="empty">No workspaces yet. Create one to hold domains, mail and the rest.</p>
			)}

			<Popup open={open} title="New workspace" onClose={close}>
				<form onSubmit={onSubmit}>
					{error && <p className="err">{error}</p>}
					<label htmlFor="workspace-name">Name</label>
					<input
						id="workspace-name"
						value={name}
						onChange={(event) => setName(event.target.value)}
						required
						autoComplete="organization"
						autoFocus
					/>
					<p className="actions">
						<button className="btn" type="submit" disabled={create.isPending}>
							{create.isPending ? "Creating" : "Create workspace"}
						</button>
						<button className="btn ghost" type="button" onClick={close}>
							Cancel
						</button>
					</p>
				</form>
			</Popup>
		</div>
	);
}

export function WorkspaceAppsPage() {
	const { organisationId: routeId } = useParams();
	const setOrganisationId = useOrganisationStore((state) => state.setOrganisationId);
	const organisationId = useOrganisationStore((state) => state.organisationId);
	const queryClient = useQueryClient();
	const [selected, setSelected] = useState<WorkspaceApp[]>([]);
	const [error, setError] = useState<string | null>(null);
	const session = useSession();
	const membership = session.data?.memberships.find((item) => item.organisationId === routeId);
	const canManage = membership?.role === "owner" || membership?.role === "admin";

	useEffect(() => {
		if (!routeId || routeId === organisationId) return;
		void api
			.selectWorkspace(routeId)
			.then((result) => {
				setOrganisationId(result.organisationId);
				void queryClient.invalidateQueries();
			})
			.catch((caught) => setError(presentError(caught).title));
	}, [routeId, organisationId, queryClient, setOrganisationId]);

	const apps = useQuery({
		queryKey: ["workspace-apps", organisationId],
		queryFn: () => api.getWorkspaceApps(),
		enabled: organisationId === routeId && Boolean(routeId),
	});

	useEffect(() => {
		if (apps.data) setSelected(apps.data.apps);
	}, [apps.data]);

	const save = useMutation({
		mutationFn: () => api.setWorkspaceApps(selected),
		onSuccess: async () => {
			await queryClient.invalidateQueries();
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	function toggle(app: WorkspaceApp) {
		setSelected((current) =>
			current.includes(app) ? current.filter((item) => item !== app) : [...current, app],
		);
	}

	return (
		<div>
			<p className="sub">
				{membership?.organisationName ?? "This workspace"}. Owners and admins can open every app that is on.
			</p>
			{error && <p className="err">{error}</p>}
			{apps.isError && <p className="err">{presentError(apps.error).title}</p>}
			<div className="card">
				<div className="checks">
					{APP_OPTIONS.map((option) => (
						<label key={option.id} className="check">
							<input
								type="checkbox"
								checked={selected.includes(option.id)}
								disabled={!canManage || apps.isPending}
								onChange={() => toggle(option.id)}
							/>
							<span>
								{option.label}
								<span className="quiet"> — {option.detail}</span>
							</span>
						</label>
					))}
				</div>
				{canManage && (
					<button
						className="btn"
						type="button"
						style={{ marginTop: 14 }}
						disabled={save.isPending}
						onClick={() => save.mutate()}
					>
						{save.isPending ? "Saving" : "Save apps"}
					</button>
				)}
			</div>
		</div>
	);
}

export function ConfigPage() {
	const config = useQuery({ queryKey: ["workspace-config"], queryFn: () => api.getWorkspaceConfig() });
	const queryClient = useQueryClient();
	const [error, setError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);
	const [logo, setLogo] = useState<string | null | undefined>(undefined);

	const save = useMutation({
		mutationFn: (input: Parameters<typeof api.updateWorkspaceConfig>[0]) => api.updateWorkspaceConfig(input),
		onSuccess: async () => {
			setSaved(true);
			await queryClient.invalidateQueries();
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	if (config.isPending) return <p className="empty">Loading configuration.</p>;
	if (config.isError || !config.data) return <p className="err">{presentError(config.error).title}</p>;

	const data = config.data;
	const mailOn = data.apps.includes("mail");
	const siteOn = data.apps.includes("site");

	function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setError(null);
		setSaved(false);
		const form = new FormData(event.currentTarget);
		const quota = String(form.get("quota") ?? "").trim();
		save.mutate({
			name: String(form.get("name") ?? ""),
			legalName: String(form.get("legalName") ?? "") || null,
			registrationNumber: String(form.get("registrationNumber") ?? "") || null,
			billingEmail: String(form.get("billingEmail") ?? ""),
			country: String(form.get("country") ?? ""),
			timezone: String(form.get("timezone") ?? ""),
			...(mailOn
				? { defaultMailboxQuotaBytes: quota === "" ? null : Math.round(Number(quota) * 1024 * 1024 * 1024) }
				: {}),
			...(siteOn
				? {
						siteIndexPage: String(form.get("siteIndexPage") ?? "index.html"),
						siteErrorPage: String(form.get("siteErrorPage") ?? "404.html"),
					}
				: {}),
			...(logo !== undefined ? { logo } : {}),
		});
	}

	const quotaGb =
		data.defaultMailboxQuotaBytes === null
			? ""
			: String(Math.round((data.defaultMailboxQuotaBytes / 1024 ** 3) * 100) / 100);

	return (
		<form onSubmit={onSubmit}>
			{error && <p className="err">{error}</p>}
			{saved && <p className="sub">Configuration saved.</p>}

			<h2>Workspace</h2>
			<div className="stack">
				<label htmlFor="cfg-logo">Logo</label>
				{(logo !== undefined ? logo : data.logo) ? (
					<img className="top-logo" src={(logo !== undefined ? logo : data.logo) ?? ""} alt="" />
				) : (
					<p className="hint">Jages and Cucius use their own marks until you add one.</p>
				)}
				<input
					id="cfg-logo"
					type="file"
					accept="image/*"
					onChange={(event) => {
						const file = event.target.files?.[0];
						if (!file) return;
						if (file.size > 150_000) {
							setError("Use a logo under 150 KB.");
							return;
						}
						const reader = new FileReader();
						reader.onload = () => {
							setLogo(typeof reader.result === "string" ? reader.result : null);
							setSaved(false);
						};
						reader.readAsDataURL(file);
					}}
				/>
				{(logo !== undefined ? logo : data.logo) ? (
					<button className="btn ghost" type="button" onClick={() => setLogo(null)}>
						Remove logo
					</button>
				) : null}
				<label htmlFor="cfg-name">Name</label>
				<input id="cfg-name" name="name" defaultValue={data.name} required />
				<label htmlFor="cfg-legal">Legal name</label>
				<input id="cfg-legal" name="legalName" defaultValue={data.legalName ?? ""} />
				<label htmlFor="cfg-reg">Registration number</label>
				<input id="cfg-reg" name="registrationNumber" defaultValue={data.registrationNumber ?? ""} />
				<label htmlFor="cfg-email">Billing email</label>
				<input id="cfg-email" name="billingEmail" type="email" defaultValue={data.billingEmail} required />
				<label htmlFor="cfg-country">Country</label>
				<input id="cfg-country" name="country" defaultValue={data.country} maxLength={2} required />
				<label htmlFor="cfg-tz">Timezone</label>
				<input id="cfg-tz" name="timezone" defaultValue={data.timezone} required />
			</div>

			{mailOn && (
				<>
					<h2>Mail</h2>
					<div className="stack">
						<label htmlFor="cfg-quota">Default mailbox quota (GB)</label>
						<input
							id="cfg-quota"
							name="quota"
							inputMode="decimal"
							defaultValue={quotaGb}
							placeholder="No default"
						/>
					</div>
					<div className="card">
						<p className="sub">Connection details for email clients. These are fixed for this server.</p>
						<p>
							IMAP {data.mailClient.imap.host}:{data.mailClient.imap.port} {data.mailClient.imap.security}
						</p>
						<p>
							SMTP {data.mailClient.submissionImplicit.host}:{data.mailClient.submissionImplicit.port}{" "}
							{data.mailClient.submissionImplicit.security}
						</p>
						<p>
							SMTP {data.mailClient.submissionStartTls.host}:{data.mailClient.submissionStartTls.port}{" "}
							{data.mailClient.submissionStartTls.security}
						</p>
					</div>
				</>
			)}

			{siteOn && (
				<>
					<h2>Site</h2>
					<div className="stack">
						<label htmlFor="cfg-index">Default index page</label>
						<input id="cfg-index" name="siteIndexPage" defaultValue={data.siteIndexPage} required />
						<label htmlFor="cfg-error">Default error page</label>
						<input id="cfg-error" name="siteErrorPage" defaultValue={data.siteErrorPage} required />
					</div>
				</>
			)}

			<button className="btn" type="submit" disabled={save.isPending} style={{ marginTop: 16 }}>
				{save.isPending ? "Saving" : "Save"}
			</button>
		</form>
	);
}

export function BillingPlaceholder() {
	const overview = useQuery({ queryKey: ["workspace-overview-billing"], queryFn: () => api.getOverview() });
	const plan = overview.data?.subscription?.plan;

	return (
		<div>
			<p className="sub">
				Payment is not available yet. The storage limit still applies to every mailbox, and a failed payment
				does not delete mail.
			</p>
			{overview.isError && <p className="err">{presentError(overview.error).title}</p>}
			{overview.data && (
				<div className="card">
					<h2>{plan?.name ?? "No plan yet"}</h2>
					<p className="sub">
						{plan
							? `${plan.storageLabel} shared across this workspace.`
							: "Choose a storage plan when billing is available."}
					</p>
					<AccountStorageSummary storage={overview.data.storage} showCounts={false} />
				</div>
			)}
		</div>
	);
}

export function MailHome() {
	const storage = useQuery({ queryKey: queryKeys.storage, queryFn: () => api.getStorage() });
	const aliases = useQuery({ queryKey: queryKeys.aliases, queryFn: () => api.listAliases() });

	if (storage.isLoading || aliases.isLoading) return <p className="empty">Loading mail.</p>;
	if (storage.isError) return <p className="err">{presentError(storage.error).title}</p>;
	if (aliases.isError) return <p className="err">{presentError(aliases.error).title}</p>;
	if (!storage.data) return null;

	const aliasCount = aliases.data?.filter(isMailboxAlias).length ?? 0;
	const forwarderCount = aliases.data?.filter(isForwarder).length ?? 0;

	return (
		<div className="stats">
			<div className="stat">
				<strong>{formatBytes(storage.data.usedBytes)}</strong>
				<span>of {formatBytes(storage.data.totalBytes)} used</span>
			</div>
			<Link className="stat" to="/mail/mailboxes">
				<strong>{storage.data.mailboxCount}</strong>
				<span>Mailboxes</span>
			</Link>
			<Link className="stat" to="/mail/aliases">
				<strong>{aliasCount}</strong>
				<span>Aliases</span>
			</Link>
			<Link className="stat" to="/mail/forwarders">
				<strong>{forwarderCount}</strong>
				<span>Forwarders</span>
			</Link>
		</div>
	);
}

export function AreaNote({ children }: { children: string }) {
	return <p className="empty">{children}</p>;
}
